import { BenefitDetailResponse } from '@aslanpay/portal-common-lib';
import * as yup from 'yup';
import { isEmptyUrl } from 'app/utils/url';

export interface AddEditBenefitFormProps {
  saveDraftBenefit: (info: AddEditBenefitFormFields) => void;
  publishBenefit: (data: AddEditBenefitFormFields) => void;
  handleBack: () => void;
  benefit?: BenefitDetailResponse;
  isSaving?: boolean;
}

export interface BenefitDocumentFormField {
  id?: string;
  fileUploadId: string;
  displayName: string;
  fileName: string;
}

export interface AddEditBenefitFormFields {
  benefitName: string;
  shortDescription?: string;
  description: string;
  employeeLink?: string;
  linkName?: string;
  logoUrl: string;
  categoryIds?: string[];
  highlights?: { text: string }[];
  eligibilityCriteria?: { text: string }[];
  images?: { imageUrl: string }[];
  videoUrls?: { url: string }[];
  faqs?: { question: string; answer: string }[];
  links?: { linkUrl: string; linkName: string }[];
  documents?: BenefitDocumentFormField[];
}

export const addBenefitsSchema = yup.object().shape({
  benefitName: yup
    .string()
    .required('Benefit name is mandatory')
    .min(3, 'Benefit name must be at least 3 characters'),
  shortDescription: yup
    .string()
    .test('short-desc-length', 'Short description must be at least 3 characters', (value) => {
      if (!value || value.trim().length === 0) return true;
      return value.trim().length >= 3;
    }),
  description: yup
    .string()
    .test('desc-length', 'Description must be at least 3 characters', (value) => {
      if (!value || value.trim().length === 0) return true;
      const textOnly = value.replace(/<[^>]*>/g, '').trim();
      return textOnly.length === 0 || textOnly.length >= 3;
    }),
  employeeLink: yup.string().test('employee-link-url', 'Must be a valid URL', (value) => {
    if (isEmptyUrl(value)) return true;
    return /^https?:\/\/.+\..+/.test(value ?? '');
  }),
  linkName: yup.string().when('employeeLink', {
    is: (employeeLink: string) => !isEmptyUrl(employeeLink),
    then: (schema) =>
      schema
        .required('Link name is mandatory when employee link is provided')
        .min(3, 'Link name must be at least 3 characters'),
    otherwise: (schema) => schema,
  }),
  logoUrl: yup.string().required('Logo is mandatory'),
  highlights: yup.array().of(
    yup.object().shape({
      text: yup
        .string()
        .test('highlight-length', 'Highlight must be at least 3 characters', (value) => {
          if (!value || value.trim().length === 0) return true;
          return value.trim().length >= 3;
        }),
    })
  ),
  eligibilityCriteria: yup.array().of(
    yup.object().shape({
      text: yup
        .string()
        .test(
          'eligibility-length',
          'Eligibility criterion must be at least 3 characters',
          (value) => {
            if (!value || value.trim().length === 0) return true;
            return value.trim().length >= 3;
          }
        ),
    })
  ),
  faqs: yup.array().of(
    yup.object().shape({
      question: yup
        .string()
        .test('question-length', 'Question must be at least 3 characters', (value) => {
          if (!value || value.trim().length === 0) return true;
          return value.trim().length >= 3;
        }),
      answer: yup
        .string()
        .test('answer-length', 'Answer must be at least 3 characters', (value) => {
          if (!value || value.trim().length === 0) return true;
          return value.trim().length >= 3;
        }),
    })
  ),
  videoUrls: yup.array().of(
    yup.object().shape({
      url: yup.string().test('youtube-url', 'Must be a valid YouTube URL', (value) => {
        if (!value || value === 'https://') return true;
        return /^https:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+$/.test(value);
      }),
    })
  ),
  links: yup.array().of(
    yup.object().shape({
      linkUrl: yup.string().test('link-url', 'Must be a valid URL', (value) => {
        if (isEmptyUrl(value)) return true;
        return /^https?:\/\/.+\..+/.test(value ?? '');
      }),
      linkName: yup
        .string()
        .test(
          'link-name',
          'Button label is mandatory when a link URL is provided',
          function validateLinkName(value) {
            const { linkUrl } = this.parent as { linkUrl?: string };
            if (isEmptyUrl(linkUrl)) return true;
            if (!value || !value.trim()) return false;
            if (value.trim().length < 3)
              return this.createError({ message: 'Button label must be at least 3 characters' });
            if (value.trim().length > 50)
              return this.createError({ message: 'Button label must be at most 50 characters' });
            return true;
          }
        ),
    })
  ),
  documents: yup.array().of(
    yup.object().shape({
      fileUploadId: yup.string().required('File is required'),
      displayName: yup
        .string()
        .required('Display name is required')
        .min(3, 'Display name must be at least 3 characters')
        .max(50, 'Display name must be at most 50 characters'),
    })
  ),
});

export const DEFAULT_ADD_BENEFIT_FORM_DATA: AddEditBenefitFormFields = {
  benefitName: '',
  shortDescription: '',
  description: '',
  employeeLink: '',
  linkName: '',
  logoUrl: '',
  categoryIds: [],
  highlights: [],
  eligibilityCriteria: [],
  images: [],
  videoUrls: [],
  faqs: [],
  links: [],
  documents: [],
};
